import { C as Cookies } from './index2.js';
import { D as DomService } from './index3.js';

// ////////////////////////////////////////////////////////////////////////////////////////////// //
// /////////////////////////////////// AWS AMPLIFY WORKAROUND /////////////////////////////////// //
// ////////////////////////////////////////////////////////////////////////////////////////////// //
/**
 * This entire workaround should be replaced by the line:
 * import { Amplify, Auth } from 'aws-amplify';
 *
 * However, this is not possible due to a AWS + Rollup + Typescript compilation error where a
 * "require" function is left in the final code (i.e. not replaced by the Rollup code-splitting
 * tools), which causes the browser to throw an error because the "require" function is not defined.
 *
 * The following code imports the "core" and "auth" libraries of AWS Amplify separately because the
 * minified version of the "aws-amplify" package is 1MB - which is not acceptable for a FE app.
 * We achieve this by importing the "core" script and waiting for the variables to be available on
 * the window. Afterwards, we make the "core" variables available to other modules of Amplify and
 * load the "auth" script.
 *
 * After all the variables are available in the window, we update the local "Amplify" and "Auth"
 * variables. When this workaround is removed, the "AwsService" code will only need to remove the
 * call to "loadAwsAmplify" and the "aws-amplify-*.min.js" scripts from the /assets/scripts folder.
 */
let Amplify;
let Auth;
const awsWindow = window;
const waitForCoreLoad = async () => new Promise((resolve) => {
    const wait = () => {
        if (awsWindow.aws_amplify_core
            && awsWindow.aws_amplify_core.Amplify
            && awsWindow.aws_amplify_core.ConsoleLogger) {
            setTimeout(() => resolve());
            return;
        }
        setTimeout(wait, 10);
    };
    wait();
});
const waitForAuthLoad = async () => new Promise((resolve) => {
    const wait = () => {
        if (awsWindow.aws_amplify_auth && awsWindow.aws_amplify_auth.Auth) {
            setTimeout(() => resolve());
            return;
        }
        setTimeout(wait, 10);
    };
    wait();
});
const loadCore = async (basePath) => new Promise((resolve) => {
    const script = DomService.createElement('script', '', { src: `${basePath}assets/scripts/aws-amplify-core.min.js`, async: '', defer: '' });
    script.onload = async () => {
        await waitForCoreLoad();
        // make "core" available to other aws packages via the window
        awsWindow['@aws-amplify/core'] = awsWindow.aws_amplify_core;
        // update the local "Amplify" variable
        Amplify = awsWindow.aws_amplify_core.Amplify;
        resolve();
    };
    document.head.appendChild(script);
});
const loadAuth = async (basePath) => new Promise((resolve) => {
    const script = DomService.createElement('script', '', { src: `${basePath}assets/scripts/aws-amplify-auth.min.js`, async: '', defer: '' });
    script.onload = async () => {
        await waitForAuthLoad();
        // update the local "Auth" variable
        Auth = awsWindow.aws_amplify_auth.Auth;
        resolve();
    };
    document.head.appendChild(script);
});
const loadAwsAmplify = async (basePath) => {
    await loadCore(basePath);
    await loadAuth(basePath);
};
// ////////////////////////////////////////////////////////////////////////////////////////////// //
// ////////////////////////////////////////////////////////////////////////////////////////////// //
const AwsService = {
    async init(basePath) {
        // TODO: remove when workaround is replaced by a simple import
        await loadAwsAmplify(basePath);
        const { cognitoUserpoolId, cognitoUserpoolClientId } = DomService.getGlobalConfig();
        Amplify.configure({
            userPoolId: cognitoUserpoolId,
            userPoolWebClientId: cognitoUserpoolClientId,
        });
    },
    async isLoggedIn() {
        let user;
        try {
            user = await Auth.currentAuthenticatedUser();
        }
        catch (_) {
            return false;
        }
        const token = user ? user.getSignInUserSession().getIdToken().getJwtToken() : '';
        return !!token && Cookies.get('id_token') === token;
    },
    async login(username, password) {
        let user;
        try {
            user = await Auth.signIn(username, password);
            if (user) {
                Cookies.set('id_token', user.getSignInUserSession().getIdToken().getJwtToken());
            }
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error signing in:', error);
            user = null;
        }
        return user;
    },
    async logout(global = false) {
        try {
            await Auth.signOut({ global });
        }
        catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error signing out:', error);
        }
    },
    async register(username, password, email) {
        await Auth.signUp({
            username,
            password,
            attributes: {
                email, // optional
            },
        });
    },
};

export { AwsService as A };

//# sourceMappingURL=index10.js.map